import { createAutocomplete } from "@algolia/autocomplete-core";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia";
import { useGlobalModalContext } from "@components/GlobalModalProvider";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { CommonCMS } from "@lib/constants/contentful";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useStore } from "@store/index";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { debounced } from "./debouncedUtil";

interface UseSearchBarProps {
    searchBarOpen?: boolean;
    isDesktopView?: boolean;
}

export const useSearchBar = ({ searchBarOpen, isDesktopView }: UseSearchBarProps) => {
    const { events, push, locale } = useRouter();
    const { searchClient, indexName } = useAlgoliaClient(locale);
    const [autocompleteState, setAutocompleteState] = useState({
        isOpen: false,
        collections: [],
        query: "",
        completion: null,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { get: getMicrocopy } = useMicrocopy();

    const { hideModal } = useGlobalModalContext();

    const containerRef = useRef(null);
    const formRef = useRef(null);
    const inputRef = useRef(null);

    const searchPagePath = useMemo(() => {
        return getPagePathByLocale(searchPage, locale);
    }, [locale]);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
    }, []);

    useEffect(() => {
        if (isDesktopView) {
            if (searchBarOpen) {
                inputRef?.current?.focus();
                setAutocompleteState((prevState) => ({
                    ...prevState,
                    isOpen: true,
                }));
            } else {
                inputRef?.current?.blur();
                setAutocompleteState((prevState) => ({
                    ...prevState,
                    isOpen: false,
                }));
            }
        }
    }, [searchBarOpen, isDesktopView]);

    useEffect(() => {
        events.on("routeChangeComplete", () => {
            hideModal();
            setIsLoading(false);
        });
        return () => {
            events.off("routeChangeComplete", () => {
                hideModal();
                setIsLoading(false);
            });
        };
    }, [events, hideModal]);

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
        key: "RECENT_SEARCH",
        limit: 5,
        transformSource({ source }) {
            return {
                ...source,
                getItemUrl({ item }) {
                    return `${searchPagePath}?query=${encodeURIComponent(item.label)}`;
                },
            };
        },
    });

    const currentUser = useStore((state) => state.data.me);
    const userToken = getAlgoliaUserToken(currentUser);

    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
        searchClient,
        indexName: `${indexName}_query_suggestions`,
        getSearchParams() {
            return recentSearchesPlugin?.data?.getAlgoliaSearchParams({
                length: 5,
                offset: 0,
                highlightPreTag: "<mark>",
                highlightPostTag: "</mark>",
                userToken: userToken,
            });
        },
    });

    const autocomplete = useMemo(
        () =>
            createAutocomplete({
                id: "search-autocomplete",
                placeholder: getMicrocopy(CommonCMS.shared, CommonCMS.searchBarPlaceholder),
                openOnFocus: true,
                autoFocus: true,
                plugins: [recentSearchesPlugin, querySuggestionsPlugin],
                initialState: autocompleteState,
                onStateChange({ state }) {
                    setAutocompleteState(state);
                },

                stallThreshold: 1000,
                onSubmit(params) {
                    setIsLoading(true);
                    setAutocompleteState({
                        ...autocompleteState,
                        isOpen: false,
                    });
                    params?.setQuery(params.state.query);
                    push({
                        pathname: searchPagePath,
                        query:
                            params?.state?.query &&
                            `query=${encodeURIComponent(params?.state?.query)}`,
                    });
                    params?.refresh();
                },

                container: formRef?.current,

                // @ts-ignore
                getSources({ query }) {
                    if (!query) return [];

                    return debounced([
                        {
                            sourceId: "products",
                            getItemInputValue({ item }) {
                                return item?.query;
                            },

                            getItems({ query }) {
                                if (!isDesktopView) return [];
                                return getAlgoliaResults({
                                    searchClient,
                                    queries: [
                                        {
                                            indexName,
                                            query,
                                            params: {
                                                hitsPerPage: 3,
                                                facetFilters: ["hasStock:true"],
                                                userToken: userToken,
                                                clickAnalytics: true,
                                            },
                                        },
                                    ],
                                });
                            },
                        },
                    ]);
                },
            }),

        [userToken, isDesktopView]
    );

    return {
        autocomplete,
        searchPagePath,
        setAutocompleteState,
        recentSearchesPlugin,
        querySuggestionsPlugin,
        containerRef,
        formRef,
        inputRef,
        isLoading,
        setIsLoading,
        searchClient,
        autocompleteState,
    };
};
